import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Mail, MessageSquare, Edit3, RefreshCw, Send, Eye, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Templates that have a factory default (reset button visible only for these)
const HAS_DEFAULT = new Set([
  'reminder_7d_balance', 'reminder_2d_details', 'reminder_1d_sms', 'reminder_post_thankyou',
  'manual_party_reminder', 'manual_payment_reminder', 'manual_thankyou',
]);

const CATEGORY_LABEL = {
  reminder: 'Automated Sequence',
  manual: 'Manual Reminder',
};

const CATEGORY_COLOR = {
  reminder: '#06D6A0',
  manual: '#6366F1',
};

export const EmailTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placeholders, setPlaceholders] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited
  const [filter, setFilter] = useState('all'); // all | reminder | manual

  const load = async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/email-templates/placeholders`, { withCredentials: true }),
      ]);
      setTemplates(tRes.data || []);
      setPlaceholders(pRes.data?.placeholders || []);
    } catch {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return templates;
    if (filter === 'manual') {
      // Include "manual" + any legacy un-categorised templates (welcome, balance_reminder, etc.)
      return templates.filter(t => (t.category || 'legacy') !== 'reminder');
    }
    return templates.filter(t => t.category === filter);
  }, [templates, filter]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;
  }

  return (
    <div data-testid="email-templates-panel">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="text-sm text-[#6B6B7A] mr-2">
          Customize the subject & body of every email / SMS customers receive. Placeholders like <code className="text-[11px] bg-[#FFFDF8] px-1 py-0.5 rounded">{'{child_name}'}</code> are replaced with booking data at send time.
        </div>
      </div>
      <div className="flex gap-2 mb-5">
        {['all', 'reminder', 'manual'].map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setFilter(cat)}
            data-testid={`tpl-filter-${cat}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              filter === cat
                ? 'bg-[#FF3B6B] text-white'
                : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
            }`}
          >
            {cat === 'all' ? 'All' : CATEGORY_LABEL[cat] || cat}
            <span className="ml-1.5 text-[10px] opacity-70">
              {cat === 'all' ? templates.length : (cat === 'manual' ? templates.filter(t => (t.category || 'legacy') !== 'reminder').length : templates.filter(t => t.category === cat).length)}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((t) => {
          const cat = t.category || 'legacy';
          const color = CATEGORY_COLOR[cat] || '#6B6B7A';
          const Icon = t.channel === 'sms' ? MessageSquare : Mail;
          return (
            <div key={t.template_id} className="bg-white rounded-2xl border border-[#E8E5DF] p-5" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#1E1E24]">{t.name || t.template_id}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-[#E8E5DF]" style={{ color, borderColor: `${color}55` }}>
                      {CATEGORY_LABEL[cat] || 'Legacy'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-[#E8E5DF] text-[#6B6B7A]">
                      {(t.channel || 'email').toUpperCase()}
                    </Badge>
                    <code className="text-[10px] text-[#6B6B7A]">{t.template_id}</code>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full text-xs border-[#E8E5DF]"
                  onClick={() => setEditing(t)}
                  data-testid={`template-edit-${t.template_id}`}
                >
                  <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              </div>
              {t.subject && (
                <p className="text-xs font-medium text-[#1E1E24] mb-1 truncate">Subject: <span className="text-[#6B6B7A] font-normal">{t.subject}</span></p>
              )}
              <div className="text-xs text-[#6B6B7A] bg-[#FFFDF8] rounded-lg p-3 border border-[#F0EDE6] max-h-24 overflow-hidden relative">
                <div className="line-clamp-4 font-mono text-[11px]">{(t.body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 260) || '(empty)'}…</div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <TemplateEditDialog
          template={editing}
          placeholders={placeholders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------
const TemplateEditDialog = ({ template, placeholders, onClose, onSaved }) => {
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const [focusField, setFocusField] = useState('body'); // 'body' | 'subject'

  const insertPlaceholder = (ph) => {
    const token = `{${ph}}`;
    const target = focusField === 'subject' ? subjectRef.current : bodyRef.current;
    if (!target) {
      if (focusField === 'subject') setSubject((s) => s + token);
      else setBody((b) => b + token);
      return;
    }
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    const current = target.value;
    const next = current.slice(0, start) + token + current.slice(end);
    if (focusField === 'subject') setSubject(next); else setBody(next);
    setTimeout(() => {
      target.focus();
      const pos = start + token.length;
      target.setSelectionRange(pos, pos);
    }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/email-templates`, {
        template_id: template.template_id,
        name: template.name,
        category: template.category,
        channel: template.channel,
        subject,
        body,
      }, { withCredentials: true });
      toast.success('Template saved — future sends will use the new content');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Restore this template to its factory default? Your custom content will be lost.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/email-templates/${template.template_id}/reset`, {}, { withCredentials: true });
      setSubject(res.data.template.subject || '');
      setBody(res.data.template.body || '');
      toast.success('Reset to default');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const testSend = async () => {
    setTesting(true);
    try {
      // Ensure we're testing the currently-typed draft — save first, then trigger test.
      await axios.post(`${API_URL}/api/admin/email-templates`, {
        template_id: template.template_id,
        name: template.name,
        category: template.category,
        channel: template.channel,
        subject,
        body,
      }, { withCredentials: true });
      const res = await axios.post(`${API_URL}/api/admin/email-templates/${template.template_id}/test-send`, {}, { withCredentials: true });
      toast.success(`Test ${res.data.channel === 'sms' ? 'SMS' : 'email'} sent to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const canReset = HAS_DEFAULT.has(template.template_id);
  const isSMS = template.channel === 'sms';

  // Live preview with a synthetic merge
  const previewHtml = useMemo(() => {
    const sample = {
      customer_name: 'Jane Doe',
      customer_email: 'jane@example.com',
      customer_phone: '+1 (905) 616-7617',
      child_name: 'Avery',
      child_age: '8',
      party_date: '2026-05-15',
      party_time: '1:00 PM',
      package_name: 'Deluxe Fun Creator',
      guest_count: '10',
      balance_due: '274.50',
      total_amount: '549.00',
      amount_paid: '274.50',
      invoice_card: '<div style="padding:12px;background:#FFEFF3;border-radius:8px;margin:12px 0"><strong>[Invoice card preview]</strong></div>',
      discount_text: '',
      discount_code: 'SAMPLE10',
    };
    const apply = (txt) => Object.entries(sample).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), txt || '');
    return { subject: apply(subject), body: apply(body) };
  }, [subject, body]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Template — {template.name || template.template_id}</DialogTitle>
          <DialogDescription>
            Changes apply instantly to future sends. Use <code className="text-[11px]">{'{placeholder}'}</code> tokens for merge fields.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Editor */}
          <div className="space-y-3">
            {!isSMS && (
              <div>
                <Label className="text-xs font-semibold text-[#1E1E24]">Subject</Label>
                <Input
                  ref={subjectRef}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setFocusField('subject')}
                  placeholder="Email subject line"
                  className="mt-1 rounded-xl border-[#E8E5DF]"
                  data-testid="template-subject-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-[#1E1E24]">
                {isSMS ? 'SMS message' : 'Body (HTML allowed)'}
              </Label>
              <Textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onFocus={() => setFocusField('body')}
                rows={isSMS ? 6 : 16}
                className="mt-1 font-mono text-xs rounded-xl border-[#E8E5DF]"
                data-testid="template-body-input"
                placeholder={isSMS ? 'Keep under 160 chars for a single SMS segment' : '<p>Hi {customer_name}, …</p>'}
              />
              {isSMS && (
                <p className="text-[11px] text-[#6B6B7A] mt-1">
                  Length: {body.length} chars ({Math.ceil(body.length / 160) || 1} SMS segment{body.length > 160 ? 's' : ''})
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#1E1E24] mb-1 block">Insert placeholder</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="placeholder-buttons">
                {placeholders.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => insertPlaceholder(ph)}
                    className="px-2 py-1 rounded-md bg-[#FFFDF8] border border-[#E8E5DF] text-[11px] font-mono text-[#1E1E24] hover:bg-[#FF3B6B]/5 hover:border-[#FF3B6B]/30 transition"
                    data-testid={`placeholder-${ph}`}
                    title={`Insert {${ph}} at cursor in the ${focusField}`}
                  >
                    {'{' + ph + '}'}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#6B6B7A] mt-1">Click to insert at cursor. Current field: <strong>{focusField}</strong>.</p>
            </div>
          </div>

          {/* Live preview */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-[#1E1E24] flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Live preview (sample data)</Label>
            <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] p-4 max-h-[480px] overflow-y-auto" data-testid="template-preview">
              {!isSMS && previewHtml.subject && (
                <div className="text-xs text-[#6B6B7A] mb-2 pb-2 border-b border-[#F0EDE6]">
                  <span className="font-semibold">Subject:</span> {previewHtml.subject}
                </div>
              )}
              {isSMS ? (
                <pre className="text-sm whitespace-pre-wrap font-sans text-[#1E1E24]">{previewHtml.body || '(empty)'}</pre>
              ) : (
                <div className="text-sm text-[#1E1E24] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml.body || '<p class="text-[#6B6B7A]">(empty)</p>') }} />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-between sm:justify-between mt-4">
          <div className="flex gap-2">
            {canReset && (
              <Button variant="outline" onClick={reset} disabled={resetting} className="rounded-full text-xs border-[#E8E5DF]" data-testid="template-reset-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> {resetting ? 'Resetting…' : 'Reset to default'}
              </Button>
            )}
            <Button variant="outline" onClick={testSend} disabled={testing} className="rounded-full text-xs border-[#E8E5DF]" data-testid="template-test-send-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> {testing ? 'Sending…' : `Test send to me`}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="template-save-btn">
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplatesPanel;
